<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue';
import { useEditorStore, EditorTool, BrushTile } from '@/stores/editor';
import { useAssetsStore } from '@/stores/assets';
import { TILE_SIZE } from '@/formats/types';

const editorStore = useEditorStore();
const assetsStore = useAssetsStore();

// Spritesheet viewer state
const spritesheetImage = ref<string | null>(null);
const spritesheetSize = ref<{ width: number; height: number }>({ width: 0, height: 0 });
const hoveredTile = ref<{ x: number; y: number } | null>(null);
const loadingSprite = ref(false);
const containerRef = ref<HTMLElement | null>(null);

// Drag selection state
const isDragging = ref(false);
const dragStart = ref<{ x: number; y: number } | null>(null);
const dragEnd = ref<{ x: number; y: number } | null>(null);
const dragThreshold = 5;
const dragButton = ref<number>(0);

// Get spritesheet image path for selected sprite
function getSpritesheetPath(spriteId: number): string | null {
    const frames = assetsStore.getFramesForSprite(spriteId);
    const entry = frames.length > 0 ? assetsStore.getAtlasEntry(spriteId, frames[0]) : null;
    return entry ? `assets/${entry.image}` : null;
}

// Load and display the spritesheet for a sprite
async function loadSpritesheet(spriteId: number): Promise<void> {
    loadingSprite.value = true;

    try {
        await assetsStore.loadSpritesForIds(new Set([spriteId]));

        const imagePath = getSpritesheetPath(spriteId);
        if (imagePath) {
            spritesheetImage.value = imagePath;

            const img = new Image();
            img.onload = () => {
                spritesheetSize.value = { width: img.width, height: img.height };
            };
            img.src = imagePath;
        }
    } finally {
        loadingSprite.value = false;
    }
}

// Watch for sprite selection changes
watch(() => editorStore.selectedPaletteSpriteId, async (spriteId) => {
    if (spriteId !== null) {
        await loadSpritesheet(spriteId);
    } else {
        spritesheetImage.value = null;
        spritesheetSize.value = { width: 0, height: 0 };
    }
}, { immediate: true });

// Which frame sits in each 32x32 cell of the sheet, read from the atlas. The
// frame table is not guaranteed to be a regular grid, and frames that are not
// a single grid-aligned tile (objects in tile PAKs) are not paintable tiles.
const cellFrames = computed(() => {
    const frameAtCell = new Map<string, number>();
    const cellOfFrame = new Map<number, { x: number; y: number }>();
    const spriteId = editorStore.selectedPaletteSpriteId;
    if (spriteId === null) return { frameAtCell, cellOfFrame };

    for (const frame of assetsStore.getFramesForSprite(spriteId)) {
        const rect = assetsStore.getAtlasEntry(spriteId, frame)?.frame;
        if (!rect || rect.w !== TILE_SIZE || rect.h !== TILE_SIZE) continue;
        if (rect.x % TILE_SIZE !== 0 || rect.y % TILE_SIZE !== 0) continue;

        const cell = { x: rect.x / TILE_SIZE, y: rect.y / TILE_SIZE };
        const key = `${cell.x},${cell.y}`;
        // Sheets repeat some cells under several frame numbers - keep the first
        if (!frameAtCell.has(key)) {
            frameAtCell.set(key, frame);
        }
        cellOfFrame.set(frame, cell);
    }

    return { frameAtCell, cellOfFrame };
});

// Frames of the selected sprite that are in the brush. A brush holds tiles
// from one sprite: their sheet layout is what gives the brush its shape.
function getBrushFrames(): Set<number> {
    const spriteId = editorStore.selectedPaletteSpriteId;
    return new Set(
        editorStore.tileBrush.filter(t => t.spriteId === spriteId).map(t => t.frame)
    );
}

function setBrushFrames(frames: Set<number>): void {
    const spriteId = editorStore.selectedPaletteSpriteId;
    if (spriteId === null) return;

    const tiles: BrushTile[] = [];
    for (const frame of frames) {
        const cell = cellFrames.value.cellOfFrame.get(frame);
        if (cell) {
            tiles.push({ offsetX: cell.x, offsetY: cell.y, spriteId, frame });
        }
    }

    editorStore.setTileBrush(tiles);
    if (tiles.length > 0) {
        editorStore.setTool(EditorTool.PAINT_TILE);
    }
}

function forEachFrameInRect(x1: number, y1: number, x2: number, y2: number, visit: (frame: number) => void): void {
    for (let cellY = Math.min(y1, y2); cellY <= Math.max(y1, y2); cellY++) {
        for (let cellX = Math.min(x1, x2); cellX <= Math.max(x1, x2); cellX++) {
            const frame = cellFrames.value.frameAtCell.get(`${cellX},${cellY}`);
            if (frame !== undefined) {
                visit(frame);
            }
        }
    }
}

// Toggle a tile in/out of the brush
function toggleTileAt(cellX: number, cellY: number): void {
    const frame = cellFrames.value.frameAtCell.get(`${cellX},${cellY}`);
    if (frame === undefined) return;

    const frames = getBrushFrames();
    if (!frames.delete(frame)) {
        frames.add(frame);
    }
    setBrushFrames(frames);
}

// Add all tiles in a rectangle to the brush
function addRectangleToBrush(x1: number, y1: number, x2: number, y2: number): void {
    const frames = getBrushFrames();
    forEachFrameInRect(x1, y1, x2, y2, frame => frames.add(frame));
    setBrushFrames(frames);
}

// Remove all tiles in a rectangle from the brush
function removeRectangleFromBrush(x1: number, y1: number, x2: number, y2: number): void {
    const frames = getBrushFrames();
    forEachFrameInRect(x1, y1, x2, y2, frame => frames.delete(frame));
    setBrushFrames(frames);
}

// Get pixel coords relative to the spritesheet, from any mouse event
function getSpritesheetCoords(e: MouseEvent): { x: number; y: number } | null {
    const container = containerRef.value;
    if (!container) return null;

    const rect = container.getBoundingClientRect();
    return {
        x: e.clientX - rect.left + container.scrollLeft,
        y: e.clientY - rect.top + container.scrollTop,
    };
}

// Mouse down - start potential drag
function handleSpritesheetMouseDown(e: MouseEvent): void {
    if (e.button !== 0 && e.button !== 2) return;

    const coords = getSpritesheetCoords(e);
    if (!coords) return;

    if (e.button === 2) {
        e.preventDefault();
    }

    dragStart.value = coords;
    dragEnd.value = coords;
    isDragging.value = false;
    dragButton.value = e.button;

    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
}

// Mouse move during drag
function handleDragMove(e: MouseEvent): void {
    const coords = getSpritesheetCoords(e);
    if (!dragStart.value || !coords) return;

    const dx = Math.abs(coords.x - dragStart.value.x);
    const dy = Math.abs(coords.y - dragStart.value.y);
    if (dx > dragThreshold || dy > dragThreshold) {
        isDragging.value = true;
    }

    dragEnd.value = coords;
}

// Mouse up - end drag or single click
function handleDragEnd(_e: MouseEvent): void {
    document.removeEventListener('mousemove', handleDragMove);
    document.removeEventListener('mouseup', handleDragEnd);

    if (dragStart.value && dragEnd.value) {
        const startCellX = Math.floor(dragStart.value.x / TILE_SIZE);
        const startCellY = Math.floor(dragStart.value.y / TILE_SIZE);

        if (isDragging.value) {
            const endCellX = Math.floor(dragEnd.value.x / TILE_SIZE);
            const endCellY = Math.floor(dragEnd.value.y / TILE_SIZE);

            if (dragButton.value === 2) {
                removeRectangleFromBrush(startCellX, startCellY, endCellX, endCellY);
            } else {
                addRectangleToBrush(startCellX, startCellY, endCellX, endCellY);
            }
        } else {
            toggleTileAt(startCellX, startCellY);
        }
    }

    dragStart.value = null;
    dragEnd.value = null;
    isDragging.value = false;
}

function handleSpritesheetMouseMove(e: MouseEvent): void {
    const coords = getSpritesheetCoords(e);
    if (!coords) return;

    const cellX = Math.floor(coords.x / TILE_SIZE);
    const cellY = Math.floor(coords.y / TILE_SIZE);

    // Only cells holding a paintable tile highlight
    hoveredTile.value = cellFrames.value.frameAtCell.has(`${cellX},${cellY}`)
        ? { x: cellX, y: cellY }
        : null;
}

function handleSpritesheetMouseLeave(): void {
    hoveredTile.value = null;
}

// Computed for drag selection rectangle
const dragSelectionRect = computed(() => {
    if (!isDragging.value || !dragStart.value || !dragEnd.value) return null;

    const startCellX = Math.floor(dragStart.value.x / TILE_SIZE);
    const startCellY = Math.floor(dragStart.value.y / TILE_SIZE);
    const endCellX = Math.floor(dragEnd.value.x / TILE_SIZE);
    const endCellY = Math.floor(dragEnd.value.y / TILE_SIZE);

    const minCellX = Math.min(startCellX, endCellX);
    const minCellY = Math.min(startCellY, endCellY);

    return {
        left: minCellX * TILE_SIZE,
        top: minCellY * TILE_SIZE,
        width: (Math.abs(endCellX - startCellX) + 1) * TILE_SIZE,
        height: (Math.abs(endCellY - startCellY) + 1) * TILE_SIZE,
        isDeselect: dragButton.value === 2,
    };
});

// Get selected tiles for rendering overlay
const selectedTilesInViewer = computed(() => {
    const result: Array<{ x: number; y: number }> = [];

    for (const tile of editorStore.tileBrush) {
        if (tile.spriteId !== editorStore.selectedPaletteSpriteId) continue;

        const cell = cellFrames.value.cellOfFrame.get(tile.frame);
        if (cell) {
            result.push(cell);
        }
    }

    return result;
});

// Get the number of frames for the selected sprite
const frameCount = computed(() => {
    if (editorStore.selectedPaletteSpriteId === null) return 0;
    return assetsStore.getFramesForSprite(editorStore.selectedPaletteSpriteId).length;
});

// Cleanup on unmount
onUnmounted(() => {
    document.removeEventListener('mousemove', handleDragMove);
    document.removeEventListener('mouseup', handleDragEnd);
});
</script>

<template>
    <div
        class="spritesheet-viewer"
        v-if="editorStore.selectedPaletteSpriteId !== null"
    >
        <div class="viewer-header">
            <span class="viewer-title">Sprite {{ editorStore.selectedPaletteSpriteId }}</span>
            <span class="frame-count">({{ frameCount }} frames)</span>
        </div>

        <div v-if="loadingSprite" class="loading-indicator">
            Loading...
        </div>

        <div
            v-else-if="spritesheetImage"
            ref="containerRef"
            class="spritesheet-container"
            @mousedown="handleSpritesheetMouseDown"
            @mousemove="handleSpritesheetMouseMove"
            @mouseleave="handleSpritesheetMouseLeave"
            @contextmenu.prevent
        >
            <img
                :src="spritesheetImage"
                class="spritesheet-image"
                draggable="false"
            />
            <!-- Grid overlay -->
            <div class="grid-overlay" :style="{
                width: spritesheetSize.width + 'px',
                height: spritesheetSize.height + 'px',
            }">
                <!-- Selected tiles in brush -->
                <div
                    v-for="(tile, index) in selectedTilesInViewer"
                    :key="'selected-' + index"
                    class="tile-selected"
                    :style="{
                        left: tile.x * 32 + 'px',
                        top: tile.y * 32 + 'px',
                    }"
                ></div>
                <!-- Drag selection rectangle -->
                <div
                    v-if="dragSelectionRect"
                    class="drag-selection"
                    :class="{ deselect: dragSelectionRect.isDeselect }"
                    :style="{
                        left: dragSelectionRect.left + 'px',
                        top: dragSelectionRect.top + 'px',
                        width: dragSelectionRect.width + 'px',
                        height: dragSelectionRect.height + 'px',
                    }"
                ></div>
                <!-- Hover highlight -->
                <div
                    v-if="hoveredTile && !isDragging"
                    class="tile-hover"
                    :style="{
                        left: hoveredTile.x * 32 + 'px',
                        top: hoveredTile.y * 32 + 'px',
                    }"
                ></div>
            </div>
        </div>

        <!-- Selection info footer -->
        <div class="selection-info">
            <div class="brush-info">
                <span>Brush: {{ editorStore.tileBrush.length }} tile{{ editorStore.tileBrush.length !== 1 ? 's' : '' }}</span>
                <span v-if="editorStore.tileBrush.length > 0" class="brush-size">
                    ({{ editorStore.tileBrushBounds.width }}x{{ editorStore.tileBrushBounds.height }})
                </span>
            </div>
            <button
                v-if="editorStore.tileBrush.length > 0"
                class="clear-btn"
                @click="editorStore.clearTileBrush()"
                title="Clear brush selection"
            >
                Clear
            </button>
        </div>

    </div>
    <div v-else class="no-selection">
        <p>Select a sprite from the palette to view its tiles</p>
    </div>
</template>

<style scoped>
.spritesheet-viewer {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    background: var(--bg-panel);
}

.viewer-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    font-size: 11px;
    font-weight: 600;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-color);
    flex-shrink: 0;
}

.viewer-title {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.frame-count {
    font-weight: normal;
    text-transform: none;
    white-space: nowrap;
}

.loading-indicator {
    padding: 16px;
    text-align: center;
    color: var(--text-secondary);
    font-size: 12px;
}

.spritesheet-container {
    flex: 1;
    overflow: auto;
    position: relative;
    cursor: crosshair;
    background:
        linear-gradient(45deg, #2a2a3a 25%, transparent 25%),
        linear-gradient(-45deg, #2a2a3a 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #2a2a3a 75%),
        linear-gradient(-45deg, transparent 75%, #2a2a3a 75%);
    background-size: 16px 16px;
    background-position: 0 0, 0 8px, 8px -8px, -8px 0px;
    background-color: #1e1e2e;
}

.spritesheet-image {
    display: block;
    image-rendering: pixelated;
    image-rendering: crisp-edges;
}

.grid-overlay {
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
    background-image:
        linear-gradient(to right, rgba(255, 255, 255, 0.1) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
    background-size: 32px 32px;
}

.tile-selected {
    position: absolute;
    width: 32px;
    height: 32px;
    border: 2px solid #22c55e;
    background: rgba(34, 197, 94, 0.3);
    box-sizing: border-box;
}

.drag-selection {
    position: absolute;
    border: 2px dashed #f59e0b;
    background: rgba(245, 158, 11, 0.2);
    box-sizing: border-box;
    pointer-events: none;
}

.drag-selection.deselect {
    border-color: #ef4444;
    background: rgba(239, 68, 68, 0.2);
}

.tile-hover {
    position: absolute;
    width: 32px;
    height: 32px;
    border: 2px solid var(--accent-color);
    background: rgba(99, 102, 241, 0.3);
    box-sizing: border-box;
}

.selection-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: var(--bg-secondary);
    border-top: 1px solid var(--border-color);
    font-size: 11px;
    color: var(--text-secondary);
    flex-shrink: 0;
}

.brush-info {
    display: flex;
    gap: 8px;
}

.brush-size {
    color: var(--text-secondary);
}

.clear-btn {
    padding: 4px 8px;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: 4px;
    color: var(--text-secondary);
    font-size: 11px;
    cursor: pointer;
    transition: all 0.15s ease;
}

.clear-btn:hover {
    background: #ef4444;
    border-color: #ef4444;
    color: white;
}

.no-selection {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: 16px;
    color: var(--text-secondary);
    font-size: 12px;
    text-align: center;
}
</style>
